import {
  collection,
  doc,
  getDocs,
  getDocsFromCache,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { contactFromFirestore, type Contact } from "./domain";
import { db } from "./firebase";

function contactsQuery(userId: string) {
  return query(collection(db, "users", userId, "contacts"), orderBy("displayName"));
}

function contactsFromSnapshot(snapshot: Awaited<ReturnType<typeof getDocs>>): Contact[] {
  return snapshot.docs.map((document) => contactFromFirestore(
    document.id,
    document.data() as Record<string, unknown>,
  ));
}

export async function fetchLegacyContacts(userId: string): Promise<Contact[]> {
  return contactsFromSnapshot(await getDocs(contactsQuery(userId)));
}

export async function fetchCachedLegacyContacts(userId: string): Promise<Contact[]> {
  return contactsFromSnapshot(await getDocsFromCache(contactsQuery(userId)));
}

export interface ContactRelationshipUpdate {
  relationshipStrength: number;
  followUpFlag: boolean;
  followUpDate: string;
  followUpNote: string;
}

export async function updateContactRelationship(userId: string, contactId: string, update: ContactRelationshipUpdate) {
  await updateDoc(doc(db, "users", userId, "contacts", contactId), {
    ...update,
    relationshipStrength: Math.max(0, Math.min(5, Math.round(update.relationshipStrength))),
    updatedAt: serverTimestamp(),
  });
}

export async function updateContactPrivacy(userId: string, contactId: string, confidentiality: string) {
  await updateDoc(doc(db, "users", userId, "contacts", contactId), { confidentiality, updatedAt: serverTimestamp() });
}

export async function completeContactFollowUp(userId: string, contactId: string) {
  await updateDoc(doc(db, "users", userId, "contacts", contactId), {
    followUpFlag: false,
    followUpDate: "",
    followUpNote: "",
    lastFollowedUpAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function snoozeContactFollowUp(userId: string, contactId: string, days = 7) {
  const date = new Date(); date.setDate(date.getDate() + days);
  const followUpDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  await updateDoc(doc(db, "users", userId, "contacts", contactId), { followUpFlag: true, followUpDate, updatedAt: serverTimestamp() });
  return followUpDate;
}

export async function clearContactFollowUp(userId: string, contactId: string) {
  await updateDoc(doc(db, "users", userId, "contacts", contactId), { followUpFlag: false, followUpDate: "", followUpNote: "", updatedAt: serverTimestamp() });
}
